import { useFormik } from 'formik';
import { Dispatch, FC, SetStateAction, useMemo } from 'react';
import styled from 'styled-components';
import { colors } from '../../../../component/colors';
import { DropDownWithLabel } from '../../../../component/dropDown';
import { ImagePicker } from '../../../../component/imagePicker';
import { Input } from '../../../../component/input';
import { AddModal } from '../../../../component/modal';
import { TextAreaWithLabel } from '../../../../component/textArea';
import {
  useAddProductsMutation,
  useGetCategoriesQuery
} from '../../../../store/shopping/api';
import { showErrorToast, showSuccessToast } from '../../../../utils/toast';
import { addProductValidation } from '../../validation';

type IAddProducts = {
  show?: boolean;
  storeId: string;
  setShow: Dispatch<SetStateAction<number | null>>;
};
type FormikValues = {
  name: string;
  category: {
    name: string;
    index: number;
  };
  description: string;
  price: string;
  stock: number | string;
  file: File[] | null;
};
export const AddProducts: FC<IAddProducts> = ({ setShow, storeId }) => {
  const [addProduct, { isLoading: isAdding }] = useAddProductsMutation();
  const { data: allCategories } = useGetCategoriesQuery();
  const handleFilePicked = (file: File[]) => {
    setFieldValue('file', file);
  };
  const categoryList = useMemo(() => {
    return allCategories?.data.map((category) => category.name) ?? [];
  }, [allCategories]);

  const handleAddProducts = (values: FormikValues) => {
    if (!allCategories?.data) return;
    if (!values.file) {
      showErrorToast('image is required');
      return;
    }
    const formData = new FormData();
    formData.append('name', values.name.toLowerCase());
    formData.append('category', allCategories?.data[values.category.index]._id);
    formData.append('price', values.price);
    formData.append('stock', values.stock.toString());
    formData.append('description', values.description);
    values.file.forEach((file) => {
      formData.append('file', file);
    });
    formData.append('store', storeId);
    addProduct(formData)
      .unwrap()
      .then((res) => {
        showSuccessToast(res.message);
        setShow(null);
      })
      .catch((error) => {
        showErrorToast(error.message);
      });
  };
  const { values, errors, touched, setFieldValue, handleChange, handleSubmit } =
    useFormik({
      initialValues: {
        name: '',
        category: { name: '', index: 0 },
        price: '',
        stock: '',
        description: '',
        file: null
      },
      validationSchema: addProductValidation,
      onSubmit: handleAddProducts
    });

  return (
    <AddModal
      title="Add Product"
      disableBtn={isAdding}
      btnTxt={isAdding ? 'Loading...' : 'Add Product'}
      btnClick={handleSubmit}
      closeModal={() => setShow(null)}
      isLoading={isAdding}
    >
      <Container>
        <Row>
          <CustomInput
            labelText="Name"
            value={values.name}
            placeholder={'Enter product name'}
            onChange={handleChange('name')}
            errorMessage={touched.name ? errors.name : ''}
          />

          <CustomDropDown
            label="Category"
            value={values.category.name}
            onSelect={(item, index) => {
              setFieldValue('category', { name: item, index });
            }}
            options={categoryList}
            errorMessage={
              touched.category && errors.category ? '*required' : ''
            }
          />
        </Row>

        <Row>
          <CustomInput
            labelText={'Price'}
            value={values.price}
            onChange={handleChange('price')}
            errorMessage={touched.price ? errors.price : ''}
          />
          <CustomInput
            labelText={'Stock'}
            value={values.stock}
            type={'number'}
            min={0}
            onChange={handleChange('stock')}
            errorMessage={touched.stock ? errors.stock : ''}
          />
        </Row>
        <CustomTextAreaWithLabel
          labelText="Description"
          placeholder="Enter description"
          value={values.description}
          onChange={handleChange('description')}
          errorMessage={touched.description ? errors.description : ''}
        />
        <CustomImageUpload onFilePicked={handleFilePicked} />
      </Container>
    </AddModal>
  );
};

const Container = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 0px 40px;
  padding-bottom: 32px;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;

  @media screen and (max-width: 500px) {
    display: block;
  }
`;

const CustomInput = styled(Input)`
  flex: 0.46;
  color: ${colors.textDark};
  margin-bottom: 16px;
`;
const CustomDropDown = styled(DropDownWithLabel)`
  flex: 0.46;
  margin-bottom: 16px;
`;
const CustomImageUpload = styled(ImagePicker)``;

const CustomTextAreaWithLabel = styled(TextAreaWithLabel)`
  margin-bottom: 16px;
`;
